import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { OrganizationMemberRoleName } from "@multi-chat/shared";
import { LayoutGroup, motion, useReducedMotion } from "../../lib/motion.js";
import { cn } from "../../lib/utils.js";
import { settingsNavItemsForRole } from "./settings-nav.js";

export function SettingsSectionNav({
  role,
  onNavigate,
  className,
  tone = "surface",
}: {
  role: OrganizationMemberRoleName;
  onNavigate?: () => void;
  className?: string;
  tone?: "surface" | "forest";
}): React.JSX.Element {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const items = settingsNavItemsForRole(role);

  return (
    <LayoutGroup id="settings-section-nav">
    <nav
      className={cn(
        "inline-flex max-w-full items-center gap-1 overflow-x-auto rounded-2xl border p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        tone === "forest"
          ? "border-white/10 bg-[#132a27] text-white"
          : "border-line/70 bg-canvas/85 backdrop-blur-sm",
        className,
      )}
      aria-label={t("settings.nav.label")}
      data-testid="settings-section-nav"
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.id}
            to={item.to}
            onClick={onNavigate}
            data-testid={item.testId}
            className={({ isActive }) =>
              cn(
                "group relative inline-flex h-8.5 shrink-0 items-center gap-2 rounded-xl px-3 text-[0.8125rem] no-underline sm:px-3.5",
                tone === "forest"
                  ? isActive
                    ? "font-semibold text-white"
                    : "font-medium text-white/70 hover:text-white"
                  : isActive
                    ? "font-semibold text-white"
                    : "font-medium text-muted hover:bg-surface/80 hover:text-foreground",
              )
            }
          >
            {({ isActive }) => (
              <>
                {isActive ? (
                  <motion.span
                    layoutId={reduceMotion ? undefined : "settings-section-tab"}
                    className={cn(
                      "absolute inset-0 rounded-xl",
                      tone === "forest"
                        ? "bg-white/12 shadow-xs"
                        : "bg-[#132a27] shadow-[0_2px_8px_-2px_rgb(19_42_39/0.3)]",
                    )}
                    transition={
                      reduceMotion
                        ? { duration: 0 }
                        : { type: "spring", bounce: 0.16, duration: 0.38 }
                    }
                  />
                ) : null}
                <Icon
                  size={15}
                  aria-hidden="true"
                  className={cn(
                    "relative z-10 shrink-0 transition-colors",
                    isActive
                      ? "text-[#34d399]"
                      : tone === "forest"
                        ? "text-white/60 group-hover:text-white"
                        : "text-muted group-hover:text-foreground",
                  )}
                />
                <span className="relative z-10 whitespace-nowrap">{t(item.labelKey)}</span>
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
    </LayoutGroup>
  );
}
